'use client';
import PagesHeader from '@/components/PagesHeader/PagesHeader';
import { PublicSignUpDialog } from '@/components/public/PublicSignupDialog';
import { Button } from '@/components/ui';
import { sampleListings } from '@/constants/staticPublicMarketplace';
import {
  distances,
  sortByValues,
} from '@/modules/marketplace/marketplaceConfig';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

// Provider and "in search of" cards are blurred behind the signup wall.
const isTeaserBlurred = (categoryID: string) =>
  categoryID === 'providers' || categoryID === 'iso';

export default function PublicMarketplaceClient() {
  const [showPublicSignupDialog, setShowPublicSignupDialog] = useState(false);
  const router = useRouter();
  const itemsListingsTabs = [
    { label: 'All Listings', href: '' },
    {
      label: 'Your Listings',
      href: '',
    },
    { label: 'Saved Listings', href: '/public/marketplace' },
  ];
  const formatPrice = (price: string, categoryID: string) => {
    const numPrice = parseFloat(price);
    if (categoryID === 'providers' || categoryID === 'iso') {
      return `$${numPrice}/hour`;
    }
    if (numPrice === 0) return 'Free';
    return `$${numPrice}`;
  };

  return (
    <div className="min-h-screen">
      <PagesHeader
        searchText={''}
        setSearchText={() => {}}
        activeTab="/public/marketplace"
        searchPlaceholder="Search Saved Listings"
        tabs={itemsListingsTabs}
        showAllFilters={true}
        selectedCategory={''}
        setSelectedCategory={() => {}}
        isLoadingCategories={false}
        categories={['']}
        showFreeOnly={true}
        freeOnly={false}
        setFreeOnly={() => {}}
        sortBy={'Sort By :Date (Newest First)'}
        setSortBy={() => {}}
        sortByValues={sortByValues}
        maxDistanceMiles={0}
        setMaxDistanceMiles={() => {}}
        distances={distances}
      />

      <div className="xl:w-[90%] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {sampleListings.map((listing, index) => (
          <div
            key={listing.id}
            className="animate-in fade-in-0 slide-in-from-bottom-4 duration-500"
            style={{
              animationDelay: `${Math.min(index * 100, 1000)}ms`,
              animationFillMode: 'both',
            }}
          >
            <div>
              {/* The sample PNGs behind these cards are multi-megapixel
                  originals (up to 4MB each) rendered into a ~300x192 slot, so
                  a plain <img> made a logged-out visitor download ~19MB of the
                  landing experience. next/image resizes and re-encodes to
                  WebP/AVIF at the width the card actually uses; `sizes` tells
                  it which width that is at each grid breakpoint. */}
              <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gray-100">
                <Image
                  src={
                    listing.media && listing.media.length > 0
                      ? listing.media[0].url
                      : 'https://fastly.picsum.photos/id/465/200/300.jpg?hmac=GloUvp2VmlLW7pBsz3VDy5QNZizjay1SWjEmqisahZs'
                  }
                  alt={listing.title}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                  // The first row is above the fold, so it is worth preloading;
                  // everything below stays lazy.
                  priority={index < 4}
                  // These two categories are deliberately blurred for
                  // logged-out visitors, so detail in the source is wasted
                  // bytes — encode them harder.
                  quality={isTeaserBlurred(listing.categoryID) ? 35 : 70}
                  className={`object-cover ${
                    isTeaserBlurred(listing.categoryID) ? 'blur-[3px]' : ''
                  }`}
                />
              </div>
              <div className="mt-3">
                <div className="text-xl font-bold text-gray-900">
                  {formatPrice(listing.price, listing.categoryID)}
                </div>
                <div className="text-black text-base capitalize">
                  {listing.title}
                </div>
                <div className="text-sm text-black">{listing.description}</div>
              </div>
            </div>
            <PublicSignUpDialog
              open={showPublicSignupDialog}
              onOpenChange={setShowPublicSignupDialog}
              message="Join our community to view listings, save favorites, and connect with local providers!"
            />
          </div>
        ))}
      </div>

      <div className="mt-12 text-center py-8 px-4 border-t border-gray-200">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            👋 Want to see more listings?
          </h3>
          <p className="text-gray-600 mb-6 text-lg">
            Join our community to browse hundreds of items for sale, connect
            with local providers, and save your favorites!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => {
                router.push('/signup');
              }}
              className="bg-[#948780] hover:bg-[#7a6b64] px-8 py-3 rounded-full font-bold text-lg"
            >
              Sign Up
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
